"use client"

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Edit2, Copy, Check, Save, X } from 'lucide-react';
import { DatePicker, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@t-one-internal/design-system';
import type { EmployeeDetails } from '../../../stores/employeeProfileStore';
import { domainAvailabilityService } from '../../../services/domainAvailabilityService';
import { PhoneInput } from '../../../components/forms/PhoneInput';

interface AboutSectionProps {
    employee: EmployeeDetails;
    onUpdate: (updates: Partial<EmployeeDetails>) => void;
    readOnly?: boolean;
    isEditing?: boolean;
    hideTitle?: boolean;
    disableEdit?: boolean;
    onEditingChange?: (editing: boolean) => void;
}

const genders = ['Male', 'Female', 'Non Binary'];
const races = ['American Indian or Native Alaskan', 'Asian', 'Black or African American', 'Native Hawaiian or other Pacific Islander', 'White', 'Hispanic or Latino', 'Two or More Races', 'Not Disclosed', 'Black', 'Hispanic'];

export const AboutSection: React.FC<AboutSectionProps> = ({
    employee: _employee,
    onUpdate,
    readOnly = false,
    isEditing: externalIsEditing,
    hideTitle = false,
    disableEdit = false,
    onEditingChange,
}) => {
    const [internalIsEditing, setInternalIsEditing] = useState(false);
    const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
    const setIsEditing = setInternalIsEditing;
    const [isSaving, setIsSaving] = useState(false);
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const formRef = useRef<HTMLDivElement>(null);

    const [firstNameValue, setFirstNameValue] = useState<string>('');
    const [lastNameValue, setLastNameValue] = useState<string>('');
    const [genderValue, setGenderValue] = useState<string>('');
    const [dobValue, setDobValue] = useState<string | undefined>(undefined);
    const [raceValue, setRaceValue] = useState<string>('');
    const [phoneValue, setPhoneValue] = useState<string>('');
    const [emailValue, setEmailValue] = useState<string>('');

    const [originalValues, setOriginalValues] = useState<{
        firstName?: string; lastName?: string; gender?: string; dob?: string; race?: string; phone?: string; email?: string;
    } | null>(null);

    const [dobError, setDobError] = useState<string | null>(null);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [phoneError, setPhoneError] = useState<string | null>(null);
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [isCheckingPhone, setIsCheckingPhone] = useState(false);

    const emailCheckTimeoutRef = useRef<number | null>(null);
    const phoneCheckTimeoutRef = useRef<number | null>(null);

    const safeDisplay = (val: string | null | undefined) => (val && val !== 'null') ? val : '-';
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const formatDobMasked = (val: string | null | undefined): string => {
        if (!val || val === 'null') return '-';
        try {
            const ddMmmMatch = val.match(/^(\d{1,2})-([A-Za-z]{3})-\d{4}$/);
            if (ddMmmMatch) return `${ddMmmMatch[1].padStart(2, '0')}-${ddMmmMatch[2]}-****`;
            const d = new Date(val);
            if (isNaN(d.getTime())) return '-';
            return `${String(d.getDate()).padStart(2, '0')}-${MONTHS[d.getMonth()]}-****`;
        } catch { return '-'; }
    };
    const firstNameDisplay = safeDisplay(_employee.firstName);
    const lastNameDisplay = safeDisplay(_employee.lastName);
    const genderDisplay = safeDisplay(_employee.gender);
    const dobDisplay = formatDobMasked(_employee.dateOfBirth);
    const raceDisplay = safeDisplay(_employee.race);
    const phoneDisplay = safeDisplay(_employee.phone);
    const emailDisplay = safeDisplay(_employee.email);

    const parseDateToISO = useCallback((dateStr: string | null | undefined): string | undefined => {
        if (!dateStr || dateStr === 'null') return undefined;
        try {
            const ddMmmYyyyMatch = dateStr.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
            if (ddMmmYyyyMatch) {
                const [, day, monthStr, year] = ddMmmYyyyMatch;
                const monthMap: Record<string, string> = { 'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04', 'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08', 'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12' };
                const month = monthMap[monthStr];
                if (month) return `${year}-${month}-${day.padStart(2, '0')}T00:00:00`;
            }
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return `${dateStr}T00:00:00`;
            const date = new Date(dateStr);
            if (!isNaN(date.getTime())) return date.toISOString().split('T')[0] + 'T00:00:00';
            return undefined;
        } catch { return undefined; }
    }, []);

    const validateDob = (dobISO: string | undefined): string | null => {
        if (!dobISO) return null;
        const birthDate = new Date(dobISO);
        if (isNaN(birthDate.getTime())) return null;
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
        return age < 18 ? 'Employee must be at least 18 years old' : null;
    };

    const validateEmailFormat = (email: string): string | null => {
        if (!email || email.trim().length === 0) return null;
        if (!/^[^\s@]+@[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/.test(email)) {
            const domainPart = email.split('@')[1];
            if (domainPart && /[A-Z]/.test(domainPart)) {
                return 'Domain must be in lowercase (e.g. @example.com)';
            }
            return 'Please enter a valid email address';
        }
        return null;
    };

    const validatePhoneFormat = (phone: string): string | null => {
        if (!phone || phone.trim().length === 0) return null;
        return !/^[\+]?[1-9][\d]{0,15}$/.test(phone.replace(/[\s\-\(\)]/g, '')) ? 'Please enter a valid phone number' : null;
    };

    useEffect(() => {
        if (_employee && !isEditing) {
            const safe = (v: string | null | undefined) => (v && v !== 'null') ? v : '';
            setFirstNameValue(safe(_employee.firstName));
            setLastNameValue(safe(_employee.lastName));
            setGenderValue(safe(_employee.gender));
            setRaceValue(safe(_employee.race));
            setDobValue(parseDateToISO(_employee.dateOfBirth));
            setPhoneValue(safe(_employee.phone));
            setEmailValue(safe(_employee.email));
        }
    }, [_employee, isEditing, parseDateToISO]);

    const checkAvailability = useCallback(async (field: 'email' | 'phone', value: string) => {
        if (!value || value.trim().length === 0) {
            field === 'email' ? setEmailError(null) : setPhoneError(null);
            return;
        }
        if (field === 'email') { const err = validateEmailFormat(value); if (err) { setEmailError(err); return; } }
        else { const err = validatePhoneFormat(value); if (err) { setPhoneError(err); return; } }

        if (originalValues) {
            const original = field === 'email' ? originalValues.email : originalValues.phone;
            if (value.trim() === (original || '').trim()) { field === 'email' ? setEmailError(null) : setPhoneError(null); return; }
        }
        field === 'email' ? setIsCheckingEmail(true) : setIsCheckingPhone(true);
        try {
            const response = await (field === 'email' ? domainAvailabilityService.checkEmail(value) : domainAvailabilityService.checkPhone(value));
            let hasConflict = false, message = '';
            if (field === 'email') { hasConflict = response.conflicts.email_domain === true || response.conflicts.email_user === true; message = response.details.email_domain || response.details.email_user || ''; }
            else { hasConflict = response.conflicts.phone === true; message = response.details.phone || ''; }
            if (hasConflict && message) { field === 'email' ? setEmailError(message) : setPhoneError(message); }
            else { field === 'email' ? setEmailError(null) : setPhoneError(null); }
        } catch { field === 'email' ? setEmailError(null) : setPhoneError(null); }
        finally { field === 'email' ? setIsCheckingEmail(false) : setIsCheckingPhone(false); }
    }, [originalValues]);

    useEffect(() => {
        if (!isEditing) return;
        if (emailCheckTimeoutRef.current) clearTimeout(emailCheckTimeoutRef.current);
        if (!emailValue || emailValue.trim().length === 0) { setEmailError(null); return; }
        const formatError = validateEmailFormat(emailValue);
        if (formatError) { setEmailError(formatError); return; }
        emailCheckTimeoutRef.current = window.setTimeout(() => checkAvailability('email', emailValue), 700);
        return () => { if (emailCheckTimeoutRef.current) clearTimeout(emailCheckTimeoutRef.current); };
    }, [emailValue, isEditing, checkAvailability]);

    useEffect(() => {
        if (!isEditing) return;
        if (phoneCheckTimeoutRef.current) clearTimeout(phoneCheckTimeoutRef.current);
        if (!phoneValue || phoneValue.trim().length === 0) { setPhoneError(null); return; }
        const formatError = validatePhoneFormat(phoneValue);
        if (formatError) { setPhoneError(formatError); return; }
        phoneCheckTimeoutRef.current = window.setTimeout(() => checkAvailability('phone', phoneValue), 700);
        return () => { if (phoneCheckTimeoutRef.current) clearTimeout(phoneCheckTimeoutRef.current); };
    }, [phoneValue, isEditing, checkAvailability]);

    const prevExternalIsEditing = useRef(externalIsEditing);
    useEffect(() => {
        if (externalIsEditing && !prevExternalIsEditing.current) {
            const safe = (v: string | null | undefined) => (v && v !== 'null') ? v : '';
            setOriginalValues({ firstName: safe(_employee.firstName), lastName: safe(_employee.lastName), gender: safe(_employee.gender), race: safe(_employee.race), dob: parseDateToISO(_employee.dateOfBirth), phone: safe(_employee.phone), email: safe(_employee.email) });
            setDobError(null); setEmailError(null); setPhoneError(null);
        }
        if (!externalIsEditing && prevExternalIsEditing.current) {
            if (originalValues) handleSave(true);
        }
        prevExternalIsEditing.current = externalIsEditing;
    }, [externalIsEditing, _employee, parseDateToISO]);

    const handleEdit = () => {
        const safe = (v: string | null | undefined) => (v && v !== 'null') ? v : '';
        setOriginalValues({ firstName: safe(_employee.firstName), lastName: safe(_employee.lastName), gender: safe(_employee.gender), race: safe(_employee.race), dob: parseDateToISO(_employee.dateOfBirth), phone: safe(_employee.phone), email: safe(_employee.email) });
        setDobError(null); setEmailError(null); setPhoneError(null);
        setIsEditing(true);
        onEditingChange?.(true);
    };

    const handleCancel = () => { setIsEditing(false); setOriginalValues(null); setDobError(null); setEmailError(null); setPhoneError(null); onEditingChange?.(false); };

    const collectFormData = (): Partial<EmployeeDetails> => {
        const formData: Partial<EmployeeDetails> = {};
        if (!originalValues) return formData;
        const hasChanged = (val1: any, val2: any) => (val1 ?? '') !== (val2 ?? '');
        const dobChanged = (() => { const orig = originalValues.dob?.split('T')[0]; const curr = dobValue?.split('T')[0]; return (orig ?? '') !== (curr ?? ''); })();
        if (hasChanged(originalValues.firstName, firstNameValue)) formData.firstName = firstNameValue || undefined;
        if (hasChanged(originalValues.lastName, lastNameValue)) formData.lastName = lastNameValue || undefined;
        if (hasChanged(originalValues.gender, genderValue)) formData.gender = genderValue || undefined;
        if (hasChanged(originalValues.race, raceValue)) formData.race = raceValue || undefined;
        if (dobChanged) formData.dateOfBirth = dobValue?.split('T')[0];
        if (hasChanged(originalValues.phone, phoneValue)) formData.phone = phoneValue || undefined;
        if (hasChanged(originalValues.email, emailValue)) formData.email = emailValue || undefined;
        return formData;
    };

    const handleSave = async (skipEditingToggle = false) => {
        const dobValidationError = validateDob(dobValue);
        if (dobValidationError) { setDobError(dobValidationError); return; }
        const emailFormatError = validateEmailFormat(emailValue);
        if (emailFormatError) { setEmailError(emailFormatError); return; }
        const phoneFormatError = validatePhoneFormat(phoneValue);
        if (phoneFormatError) { setPhoneError(phoneFormatError); return; }
        if (emailError || phoneError || isCheckingEmail || isCheckingPhone) return;
        setDobError(null);
        setIsSaving(true);
        try {
            const formData = collectFormData();
            if (onUpdate && Object.keys(formData).length > 0) await onUpdate(formData);
            if (!skipEditingToggle) { setIsEditing(false); onEditingChange?.(false); }
            setOriginalValues(null);
        } catch (error) { console.error('Error saving about section:', error); }
        finally { setIsSaving(false); }
    };

    const copyToClipboard = async (text: string, fieldName: string) => {
        try { await navigator.clipboard.writeText(text); setCopiedField(fieldName); setTimeout(() => setCopiedField(null), 2000); } catch (err) { console.error(err); }
    };

    const CopyButton = ({ text, field }: { text: string, field: string }) => (
        <button onClick={() => copyToClipboard(text, field)} className="p-1 hover:bg-slate-100 rounded transition-colors shrink-0" title={`Copy ${field}`}>
            {copiedField === field ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-400 hover:text-slate-600" />}
        </button>
    );

    return (
        <div className="w-full">
            {!hideTitle && (
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-slate-900">Personal Details</h3>
                    {!readOnly && externalIsEditing === undefined && (
                        <div className="flex items-center gap-2">
                            {!isEditing ? (
                                <button onClick={handleEdit} disabled={disableEdit} className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-black bg-white hover:bg-slate-50 border border-transparent rounded-md transition-colors ${disableEdit ? 'opacity-40 cursor-not-allowed' : ''}`}>
                                    <Edit2 className="w-4 h-4" /> Edit
                                </button>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <button onClick={() => handleSave()} disabled={!!emailError || !!phoneError || isCheckingEmail || isCheckingPhone || isSaving || !!dobError} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-indigo-600 text-white hover:bg-indigo-700 rounded-md transition-colors disabled:opacity-50">
                                        <Save className="w-4 h-4" /> {isSaving ? 'Saving...' : 'Save'}
                                    </button>
                                    <button onClick={handleCancel} disabled={isSaving} className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-md transition-colors disabled:opacity-50">
                                        <X className="w-4 h-4" /> Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div ref={formRef} className="relative grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-8 w-full">
                <div className="grid grid-cols-[140px_1fr] items-center gap-4">
                    <label className="text-sm font-medium text-slate-500">First Name</label>
                    <div className="flex items-center gap-2">
                        {isEditing ? (<input type="text" value={firstNameValue} onChange={(e) => setFirstNameValue(e.target.value)} className="w-full px-3 py-2 text-sm text-slate-900 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter first name" />) : (<div className="text-sm font-medium text-slate-900">{firstNameDisplay}</div>)}
                    </div>
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center gap-4">
                    <label className="text-sm font-medium text-slate-500">Last Name</label>
                    <div className="flex items-center gap-2">
                        {isEditing ? (<input type="text" value={lastNameValue} onChange={(e) => setLastNameValue(e.target.value)} className="w-full px-3 py-2 text-sm text-slate-900 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter last name" />) : (<div className="text-sm font-medium text-slate-900">{lastNameDisplay}</div>)}
                    </div>
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center gap-4">
                    <label className="text-sm font-medium text-slate-500">Race</label>
                    <div className="flex items-center gap-2 min-w-0">
                        {isEditing ? (
                            <Select value={raceValue} onValueChange={setRaceValue}>
                                <SelectTrigger className="w-[200px] max-w-[200px] h-9 text-sm overflow-hidden" title={raceValue}>
                                    <div className="truncate text-left flex-1">
                                        <SelectValue placeholder="Select race" />
                                    </div>
                                </SelectTrigger>
                                <SelectContent className="w-[300px]">
                                    {races.map(r => (
                                        <SelectItem key={r} value={r}>
                                            <span className="block truncate" title={r}>{r}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : (<div className="text-sm font-medium text-slate-900">{raceDisplay}</div>)}
                    </div>
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center gap-4">
                    <label className="text-sm font-medium text-slate-500">Gender</label>
                    <div className="flex items-center gap-2">
                        {isEditing ? (<Select value={genderValue} onValueChange={setGenderValue}><SelectTrigger className="w-full h-9 text-sm"><SelectValue placeholder="Select gender" /></SelectTrigger><SelectContent>{genders.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select>) : (<div className="text-sm font-medium text-slate-900">{genderDisplay}</div>)}
                    </div>
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center gap-4">
                    <label className="text-sm font-medium text-slate-500">Date of Birth</label>
                    <div className="flex flex-col gap-1 w-full">
                        <div className="flex items-center gap-2">
                            {isEditing ? (<div className="w-full"><DatePicker value={dobValue} onChange={(val: string | undefined) => { setDobValue(val); setDobError(validateDob(val)); }} placeholder="Select date of birth" className={`h-9 text-sm w-full ${dobError ? 'border-red-500' : ''}`} /></div>) : (<div className="text-sm font-medium text-slate-900">{dobDisplay}</div>)}
                        </div>
                        {isEditing && dobError && <p className="text-xs text-red-600 font-medium">{dobError}</p>}
                    </div>
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center gap-4">
                    <label className="text-sm font-medium text-slate-500">Phone Number</label>
                    <div className="flex flex-col gap-1 w-full">
                        <div className="flex items-center gap-2 w-full">
                            {isEditing ? (<div className="w-full"><PhoneInput value={phoneValue} onChange={setPhoneValue} placeholder="Enter phone" className={`text-xs w-full ${phoneError ? 'border-red-500' : ''}`} /></div>) : (<div className="text-sm font-medium text-slate-900">{phoneDisplay}</div>)}
                            <CopyButton text={phoneDisplay} field="phone" />
                        </div>
                        {isEditing && phoneError && <p className="text-xs text-red-600 w-full">{phoneError}</p>}
                        {isEditing && isCheckingPhone && !phoneError && <p className="text-xs text-slate-400 w-full">Checking...</p>}
                    </div>
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center gap-4">
                    <label className="text-sm font-medium text-slate-500">Email</label>
                    <div className="flex flex-col gap-1 w-full">
                        <div className="flex items-center gap-2 w-full">
                            {isEditing ? (<input type="email" value={emailValue} onChange={(e) => setEmailValue(e.target.value)} className={`w-full px-3 py-2 text-sm text-slate-900 bg-white border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${emailError ? 'border-red-500' : 'border-slate-200'}`} placeholder="Enter email" />) : (<div className="text-sm font-medium text-slate-900">{emailDisplay}</div>)}
                            <CopyButton text={emailDisplay} field="email" />
                        </div>
                        {isEditing && emailError && <p className="text-xs text-red-600 w-full">{emailError}</p>}
                        {isEditing && isCheckingEmail && !emailError && <p className="text-xs text-slate-400 w-full">Checking...</p>}
                    </div>
                </div>
            </div>
        </div>
    );
};
